import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Import all locale files
import en from '../locales/en';
import de from '../locales/de';
import fr from '../locales/fr';
import es from '../locales/es';
import it from '../locales/it';
import nl from '../locales/nl';
import pt from '../locales/pt';
import hr from '../locales/hr';
import sl from '../locales/sl';
import ru from '../locales/ru';
import zh from '../locales/zh';
import ar from '../locales/ar';
import hi from '../locales/hi';
import ja from '../locales/ja';

const SUPPORTED_LANGS = ['hr','en','de','es','fr','it','nl','pt','sl','zh','ar','hi','ru','ja'];

const TRANSLATIONS = { en, de, fr, es, it, nl, pt, hr, sl, ru, zh, ar, hi, ja };

const LANG_NAMES = {
  hr: 'Hrvatski', en: 'English', de: 'Deutsch', es: 'Español',
  fr: 'Français', it: 'Italiano', nl: 'Nederlands', pt: 'Português',
  sl: 'Slovenščina', zh: '中文', ar: 'العربية',
  hi: 'हिन्दी', ru: 'Русский', ja: '日本語',
};

const LANG_FLAGS = {
  hr: '🇭🇷', en: '🇬🇧', de: '🇩🇪', es: '🇪🇸',
  fr: '🇫🇷', it: '🇮🇹', nl: '🇳🇱', pt: '🇧🇷',
  sl: '🇸🇮', zh: '🇨🇳', ar: '🇸🇦',
  hi: '🇮🇳', ru: '🇷🇺', ja: '🇯🇵',
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem('ct_lang');
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
    return 'hr'; // Default to Croatian
  });

  const switchLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGS.includes(newLang)) {
      setLang(newLang);
      localStorage.setItem('ct_lang', newLang);
      document.documentElement.lang = newLang;
      document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    }
  }, []);

  const t = useCallback((key) => {
    return TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;
  }, [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, switchLanguage, t, SUPPORTED_LANGS, LANG_NAMES, LANG_FLAGS, TRANSLATIONS }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export { SUPPORTED_LANGS, LANG_NAMES, LANG_FLAGS, TRANSLATIONS };
export default LanguageContext;
